import type { CharacterIllustrationName } from './names';
import type { CharacterIllustrationVariant } from './types';

/**
 * Asset filename for every name + variant, relative to
 * packages/design-system/assets/illustrations/. Pure data — platform code
 * resolves these to bundler-specific asset references.
 *
 * member-using-app-blue is the blue colourway of member-using-app; the two
 * names share one mono artwork, so both mono entries point at the same file.
 */
export const characterIllustrationAssetFiles = {
  'ai-conversation': { colour: 'ai-conversation.colour.svg', mono: 'ai-conversation.mono.svg' },
  'being-active': { colour: 'being-active.colour.svg', mono: 'being-active.mono.svg' },
  breathalyser: { colour: 'breathalyser.colour.svg', mono: 'breathalyser.mono.svg' },
  'care-team-at-work-emotional': {
    colour: 'care-team-at-work-emotional.colour.svg',
    mono: 'care-team-at-work-emotional.mono.svg',
  },
  'care-team-at-work-functional': {
    colour: 'care-team-at-work-functional.colour.svg',
    mono: 'care-team-at-work-functional.mono.svg',
  },
  'carrying-boat': { colour: 'carrying-boat.colour.svg', mono: 'carrying-boat.mono.svg' },
  'character-a': { colour: 'character-a.colour.svg', mono: 'character-a.mono.svg' },
  'character-b': { colour: 'character-b.colour.svg', mono: 'character-b.mono.svg' },
  'character-c': { colour: 'character-c.colour.svg', mono: 'character-c.mono.svg' },
  'character-d': { colour: 'character-d.colour.svg', mono: 'character-d.mono.svg' },
  cyclist: { colour: 'cyclist.colour.svg', mono: 'cyclist.mono.svg' },
  exploring: { colour: 'exploring.colour.svg', mono: 'exploring.mono.svg' },
  family: { colour: 'family.colour.svg', mono: 'family.mono.svg' },
  'feeling-support': { colour: 'feeling-support.colour.svg', mono: 'feeling-support.mono.svg' },
  fireworks: { colour: 'fireworks.colour.svg', mono: 'fireworks.mono.svg' },
  'friends-celebration': {
    colour: 'friends-celebration.colour.svg',
    mono: 'friends-celebration.mono.svg',
  },
  'getting-started-beach': {
    colour: 'getting-started-beach.colour.svg',
    mono: 'getting-started-beach.mono.svg',
  },
  'getting-started-boat': {
    colour: 'getting-started-boat.colour.svg',
    mono: 'getting-started-boat.mono.svg',
  },
  'growth-lg': { colour: 'growth-lg.colour.svg', mono: 'growth-lg.mono.svg' },
  'growth-sm': { colour: 'growth-sm.colour.svg', mono: 'growth-sm.mono.svg' },
  guidance: { colour: 'guidance.colour.svg', mono: 'guidance.mono.svg' },
  'helping-lg': { colour: 'helping-lg.colour.svg', mono: 'helping-lg.mono.svg' },
  'helping-sm': { colour: 'helping-sm.colour.svg', mono: 'helping-sm.mono.svg' },
  'high-five': { colour: 'high-five.colour.svg', mono: 'high-five.mono.svg' },
  'home-delivery': { colour: 'home-delivery.colour.svg', mono: 'home-delivery.mono.svg' },
  'how-to-use-it': { colour: 'how-to-use-it.colour.svg', mono: 'how-to-use-it.mono.svg' },
  'human-emotions': { colour: 'human-emotions.colour.svg', mono: 'human-emotions.mono.svg' },
  'ice-cream-fail': { colour: 'ice-cream-fail.colour.svg', mono: 'ice-cream-fail.mono.svg' },
  idea: { colour: 'idea.colour.svg', mono: 'idea.mono.svg' },
  juggle: { colour: 'juggle.colour.svg', mono: 'juggle.mono.svg' },
  'living-room': { colour: 'living-room.colour.svg', mono: 'living-room.mono.svg' },
  'looking-ahead-lg': { colour: 'looking-ahead-lg.colour.svg', mono: 'looking-ahead-lg.mono.svg' },
  'looking-ahead-sm': { colour: 'looking-ahead-sm.colour.svg', mono: 'looking-ahead-sm.mono.svg' },
  'man-thumbs-up': { colour: 'man-thumbs-up.colour.svg', mono: 'man-thumbs-up.mono.svg' },
  'man-waving': { colour: 'man-waving.colour.svg', mono: 'man-waving.mono.svg' },
  map: { colour: 'map.colour.svg', mono: 'map.mono.svg' },
  meditation: { colour: 'meditation.colour.svg', mono: 'meditation.mono.svg' },
  'member-taking-medication-alcohol': {
    colour: 'member-taking-medication-alcohol.colour.svg',
    mono: 'member-taking-medication-alcohol.mono.svg',
  },
  'member-taking-medication-tobacco': {
    colour: 'member-taking-medication-tobacco.colour.svg',
    mono: 'member-taking-medication-tobacco.mono.svg',
  },
  'member-taking-urine-test': {
    colour: 'member-taking-urine-test.colour.svg',
    mono: 'member-taking-urine-test.mono.svg',
  },
  'member-using-app': { colour: 'member-using-app.colour.svg', mono: 'member-using-app.mono.svg' },
  'member-using-app-blue': {
    colour: 'member-using-app-blue.colour.svg',
    mono: 'member-using-app.mono.svg',
  },
  'mindfulness-activities-relaxing': {
    colour: 'mindfulness-activities-relaxing.colour.svg',
    mono: 'mindfulness-activities-relaxing.mono.svg',
  },
  'nature-landscape': { colour: 'nature-landscape.colour.svg', mono: 'nature-landscape.mono.svg' },
  'nature-relaxation': {
    colour: 'nature-relaxation.colour.svg',
    mono: 'nature-relaxation.mono.svg',
  },
  'negative-thoughts': {
    colour: 'negative-thoughts.colour.svg',
    mono: 'negative-thoughts.mono.svg',
  },
  notifications: { colour: 'notifications.colour.svg', mono: 'notifications.mono.svg' },
  'office-space': { colour: 'office-space.colour.svg', mono: 'office-space.mono.svg' },
  'overcoming-obstacles': {
    colour: 'overcoming-obstacles.colour.svg',
    mono: 'overcoming-obstacles.mono.svg',
  },
  'peaceful-butterfly': {
    colour: 'peaceful-butterfly.colour.svg',
    mono: 'peaceful-butterfly.mono.svg',
  },
  'picking-fruit': { colour: 'picking-fruit.colour.svg', mono: 'picking-fruit.mono.svg' },
  progress: { colour: 'progress.colour.svg', mono: 'progress.mono.svg' },
  'progress-wide': { colour: 'progress-wide.colour.svg', mono: 'progress-wide.mono.svg' },
  'reading-book': { colour: 'reading-book.colour.svg', mono: 'reading-book.mono.svg' },
  'reading-card': { colour: 'reading-card.colour.svg', mono: 'reading-card.mono.svg' },
  'reading-map': { colour: 'reading-map.colour.svg', mono: 'reading-map.mono.svg' },
  relapse: { colour: 'relapse.colour.svg', mono: 'relapse.mono.svg' },
  'relationship-interaction': {
    colour: 'relationship-interaction.colour.svg',
    mono: 'relationship-interaction.mono.svg',
  },
  'relaxing-together': {
    colour: 'relaxing-together.colour.svg',
    mono: 'relaxing-together.mono.svg',
  },
  rowing: { colour: 'rowing.colour.svg', mono: 'rowing.mono.svg' },
  'sailing-together-lg': {
    colour: 'sailing-together-lg.colour.svg',
    mono: 'sailing-together-lg.mono.svg',
  },
  'sailing-together-sm': {
    colour: 'sailing-together-sm.colour.svg',
    mono: 'sailing-together-sm.mono.svg',
  },
  'save-money': { colour: 'save-money.colour.svg', mono: 'save-money.mono.svg' },
  'setting-goals-archery': {
    colour: 'setting-goals-archery.colour.svg',
    mono: 'setting-goals-archery.mono.svg',
  },
  'setting-goals-calendar': {
    colour: 'setting-goals-calendar.colour.svg',
    mono: 'setting-goals-calendar.mono.svg',
  },
  skipping: { colour: 'skipping.colour.svg', mono: 'skipping.mono.svg' },
  success: { colour: 'success.colour.svg', mono: 'success.mono.svg' },
  thinking: { colour: 'thinking.colour.svg', mono: 'thinking.mono.svg' },
  'waking-up': { colour: 'waking-up.colour.svg', mono: 'waking-up.mono.svg' },
  walking: { colour: 'walking.colour.svg', mono: 'walking.mono.svg' },
  'walking-bridge-lg': {
    colour: 'walking-bridge-lg.colour.svg',
    mono: 'walking-bridge-lg.mono.svg',
  },
  'walking-bridge-sm': {
    colour: 'walking-bridge-sm.colour.svg',
    mono: 'walking-bridge-sm.mono.svg',
  },
  'watering-plant': { colour: 'watering-plant.colour.svg', mono: 'watering-plant.mono.svg' },
  welcome: { colour: 'welcome.colour.svg', mono: 'welcome.mono.svg' },
  'well-done': { colour: 'well-done.colour.svg', mono: 'well-done.mono.svg' },
  'woman-thumbs-up': { colour: 'woman-thumbs-up.colour.svg', mono: 'woman-thumbs-up.mono.svg' },
  'woman-thumbs-up-2': {
    colour: 'woman-thumbs-up-2.colour.svg',
    mono: 'woman-thumbs-up-2.mono.svg',
  },
} as const satisfies Record<
  CharacterIllustrationName,
  Record<CharacterIllustrationVariant, string>
>;

export type CharacterIllustrationAssetFile =
  (typeof characterIllustrationAssetFiles)[CharacterIllustrationName][CharacterIllustrationVariant];
